import { useState, useEffect, useCallback } from 'react';
import './theme.css';
import { api, getToken, setToken, clearToken } from './api';
import SignIn from './views/SignIn';
import Garden from './views/Garden';
import NodeDetail from './views/NodeDetail';

/** A seedling. Drawn rather than imported so there is no asset to fetch and
 *  it inherits colour from wherever it sits. */
export function Mark({ size = 30 }) {
  return (
    <svg className="mark" width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <path d="M16 29V13" stroke="var(--soil)" strokeWidth="2" strokeLinecap="round" />
      <path d="M16 17C16 17 9 16 6.5 11.5C4 7 5 4 5 4C5 4 10 4.5 12.5 9C15 13.5 16 17 16 17Z"
            fill="var(--leaf)" />
      <path d="M16 15C16 15 22 13.5 24.5 9.5C27 5.5 26.5 3 26.5 3C26.5 3 21.5 4 19 8C16.5 12 16 15 16 15Z"
            fill="var(--leaf-bright)" />
      <circle cx="16" cy="30" r="1.5" fill="var(--soil)" opacity="0.35" />
    </svg>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [booting, setBooting] = useState(true);
  const [openNodeId, setOpenNodeId] = useState(null);

  // A stored token may be expired. Resolve it before rendering anything, so
  // the app never flashes a signed-in shell it then has to tear down.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!getToken()) {
        setBooting(false);
        return;
      }
      try {
        const me = await api.me();
        if (!cancelled) setUser(me);
      } catch {
        clearToken();
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onAuthenticated = useCallback((auth) => {
    setToken(auth.token);
    setUser({ userId: auth.userId, email: auth.email, name: auth.name });
  }, []);

  const signOut = useCallback(async () => {
    try { await api.logout(); } catch { /* the local token is what matters */ }
    clearToken();
    setUser(null);
    setOpenNodeId(null);
  }, []);

  if (booting) {
    return (
      <div className="auth-page">
        <div className="row" style={{ color: 'var(--ink-faint)' }}>
          <Mark size={22} /> <span>Opening the garden…</span>
        </div>
      </div>
    );
  }

  if (!user) return <SignIn onAuthenticated={onAuthenticated} />;

  return (
    <div className="shell">
      <header className="masthead">
        <div
          className="wordmark"
          role="button"
          tabIndex={0}
          style={{ cursor: 'pointer' }}
          onClick={() => setOpenNodeId(null)}
          onKeyDown={(e) => e.key === 'Enter' && setOpenNodeId(null)}
        >
          <Mark /> AeroGreen
        </div>
        <div className="row">
          <span className="faint">{user.name || user.email}</span>
          <button className="btn btn-quiet btn-sm" onClick={signOut}>Sign out</button>
        </div>
      </header>

      {openNodeId
        ? <NodeDetail nodeId={openNodeId} onBack={() => setOpenNodeId(null)} />
        : <Garden onOpenNode={setOpenNodeId} />}
    </div>
  );
}
